import { ChangeEvent, FormEvent, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  InputAdornment,
  IconButton
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { registerUser } from "../api/registerUser";
import { useCountryOptions } from "../utils/countries";

const RegisterPage = () => {
  const [form, setForm] = useState({
    username: "",
    email: "",
    name: "",
    surname: "",
    company: "",
    country: "",
    password: "",
    confirmPassword: ""
  });
  const [status, setStatus] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { setSession, resolveApiPath, userManagementEnabled } = useAuth();
  const countryOptions = useCountryOptions();
  const selectedCountry = useMemo(
    () => countryOptions.find((option) => option.code === form.country.trim().toUpperCase()) ?? null,
    [countryOptions, form.country]
  );

  if (!userManagementEnabled) {
    return <Navigate to="/player" replace />;
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus(null);
    if (form.password !== form.confirmPassword) {
      setStatus({ type: "error", message: "Passwords do not match." });
      return;
    }
    const country = form.country.trim().toUpperCase();
    if (country && country.length !== 2) {
      setStatus({ type: "error", message: "Country must be a 2-letter code." });
      return;
    }
    setSubmitting(true);
    try {
      const trimmed = {
        username: form.username.trim(),
        email: form.email.trim(),
        name: form.name.trim(),
        surname: form.surname.trim(),
        company: form.company.trim(),
        country,
        password: form.password,
        userGroups: []
      };

      const result = await registerUser(resolveApiPath, trimmed);
      if (result.ok) {
        if (result.user) {
          setSession({ user: result.user, tokens: null });
        }
        setStatus({ type: "success", message: "Account created successfully." });
        setForm({
          username: "",
          email: "",
          name: "",
          surname: "",
          company: "",
          country: "",
          password: "",
          confirmPassword: ""
        });
      } else {
        setStatus({ type: "error", message: result.error });
      }
    } catch (error) {
      setStatus({ type: "error", message: "Unable to register. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="section" maxWidth={520} mx="auto">
      <Typography variant="h4" gutterBottom>
        Create an account
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Self-service registration can be disabled by administrators.
      </Typography>
      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2, display: "grid", gap: 2 }}>
        <TextField label="Username" name="username" value={form.username} onChange={handleChange} required />
        <TextField label="Email" name="email" type="email" value={form.email} onChange={handleChange} required />
        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
          <TextField label="Name" name="name" value={form.name} onChange={handleChange} />
          <TextField label="Surname" name="surname" value={form.surname} onChange={handleChange} />
        </Box>
        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "minmax(0, 60%) minmax(0, 40%)" }}>
          <TextField label="Company" name="company" value={form.company} onChange={handleChange} />
          <Autocomplete
            options={countryOptions}
            value={selectedCountry}
            onChange={(_event, option) => setForm((prev) => ({ ...prev, country: option?.code ?? "" }))}
            getOptionLabel={(option) => option.name}
            renderOption={(props, option) => (
              <li {...props} key={option.code}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {option.Flag ? <option.Flag width={22} /> : null}
                  <Typography variant="body2">{option.name}</Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Country"
                placeholder="Select country"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: selectedCountry?.Flag ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 0.5 }}>
                      <selectedCountry.Flag width={20} />
                      {params.InputProps.startAdornment}
                    </Box>
                  ) : (
                    params.InputProps.startAdornment
                  )
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            clearOnBlur
          />
        </Box>
        <TextField
          label="Password"
          name="password"
          type={showPassword ? "text" : "password"}
          value={form.password}
          onChange={handleChange}
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        <TextField
          label="Confirm password"
          name="confirmPassword"
          type={showConfirm ? "text" : "password"}
          value={form.confirmPassword}
          onChange={handleChange}
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirm((prev) => !prev)} edge="end">
                  {showConfirm ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        <Button type="submit" variant="contained" disabled={submitting}>
          Register
        </Button>
      </Box>
      {status &&
        <Box sx={{ mt: 2, display: "grid", gap: 2 }}>
          <Alert severity={status.type}>{status.message}</Alert>
        </Box>
      }
    </Box>
  );
};

export default RegisterPage;
